import apiService from '../services/apiService';
import logger from './logger';
import * as appStorage from './appIndexedDB';
import { extractMcpAppsFromToolList } from './mcpApps';
import { authorizedRequest } from '../services/microsoftIntegrationService';

/**
 * 查詢目前登入使用者的客服 session 狀態
 * 呼叫後端 GET /web/cs/agent/session-status，依 Redis account:room 映射判斷。
 * 前端在頁面重新整理後呼叫，以確認是否需恢復 WebSocket（human agent）模式。
 *
 * @returns {{ active: boolean, roomId?: string }} session 狀態物件
 * @throws {Error} 若 API 呼叫失敗則拋出，呼叫端應自行捕捉並 fallback
 */
export const getCustomerServiceSessionStatus = async () => {
  const token = appStorage.getItemSync('mygenie_token');
  const response = await apiService.request('/web/cs/agent/session-status', {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  // response 為後端 ApiResponse 格式：{ code, message, data: { active, roomId? } }
  return response?.data ?? { active: false };
};

/**
 * 裝置心跳：呼叫後端 POST /web/device/heartbeat，更新使用者-裝置對應關係的最近使用時間。
 * 前端在頁面載入（登入完成或沿用既有 token）後呼叫一次，涵蓋「開啟網頁沿用舊 token、
 * 未觸發登入流程」的情況（此時不會經過 login/refresh 等會記錄裝置的端點）。
 * 與 session-status 分開呼叫，避免裝置紀錄與客服會話狀態這兩個不相關的關注點耦合在一起。
 *
 * @returns {Promise<void>} 失敗時拋出，呼叫端應自行捕捉並忽略（裝置心跳非關鍵路徑）
 */
export const recordDeviceHeartbeat = async () => {
  const token = appStorage.getItemSync('mygenie_token');
  await apiService.request('/web/device/heartbeat', {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
};

/** 與 `REACT_APP_API_BASE_URL`（BFF）組合為完整 URL */
export const CHAT_STARTERS_PATH = '/web/chat/starters';

/** 系統參數：MyBro 輸入區底部警語（契約見 openspec/specs/sys-param-api） */
export const SYS_PARAMS_MYBRO_WARNING_PATH =
  '/web/sys-params?paramType=mybro_wording&paramSubType=warning_wording';

/**
 * 取得聊天輸入區底部警語文案（有 token 時附帶 Authorization）。
 * 僅在 `code === 200` 且 `data` 為非空陣列、且第一筆之 `param_value`／`paramValue` trim 後非空時回傳字串；其餘回傳 `null`（呼叫端使用後援）。
 *
 * @returns {Promise<string|null>}
 */
export const getMybroWarningWording = async () => {
  const token = appStorage.getItemSync('mygenie_token');
  try {
    const response = await apiService.request(SYS_PARAMS_MYBRO_WARNING_PATH, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (response?.code !== 200 || response?.data == null) {
      return null;
    }
    const raw = response.data;
    if (!Array.isArray(raw) || raw.length === 0) {
      return null;
    }
    const first = raw[0];
    if (first == null || typeof first !== 'object') {
      return null;
    }
    const value =
      typeof first.param_value === 'string'
        ? first.param_value
        : typeof first.paramValue === 'string'
          ? first.paramValue
          : '';
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  } catch (error) {
    logger.warn('API', '取得聊天區警語失敗', {
      error: error?.message || error,
    });
    return null;
  }
};

/**
 * 取得聊天開頭語列表（可匿名；有 token 時一併送出）。
 * 契約見 openspec/specs/chat-starter-query；失敗或無資料時回傳空陣列。
 *
 * @returns {Promise<Array<{ seqNo?: number, starterLabel: string, sortOrder?: number }>>}
 */
export const getChatStarters = async () => {
  const token = appStorage.getItemSync('mygenie_token');
  try {
    const response = await apiService.request(CHAT_STARTERS_PATH, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (response?.code !== 200 || response?.data == null) {
      return [];
    }
    const raw = response.data;
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .filter(
        (item) =>
          item &&
          typeof item.starter_label === 'string' &&
          item.starter_label.trim().length > 0
      )
      .map((item) => ({
        seqNo: item.seq_no,
        starterLabel: item.starter_label.trim(),
        sortOrder: item.sort_order,
      }));
  } catch (error) {
    logger.warn('API', '取得聊天開頭語失敗', {
      error: error?.message || error,
    });
    return [];
  }
};

// 發送訊息到後端 API（attachments 為選填，v3 api-version 才有效；signal 可傳入 AbortController.signal 以取消請求；
// messageId 為前端產生的使用者訊息 UUID，供後端例外發生時對應回這則訊息並寫入 ag_tb_error_log）
// messageIds（選填）：{ userMessageId, assistantMessageId } 前端預生成訊息 id，
// 供後端回覆暫存與 idempotency（change: ai-response-server-persistence）
// sourceChannel（選填，change: notification-deeplink-chat-trigger）：對話來源管道，
// 僅供推播深連結消費後建立新對話時使用，一般打字送出不會帶
export const sendToGemini = async (message, chatHistory, chatId, attachments = [], signal = null, messageId = null, messageIds = null, sourceChannel = null) => {
  try {
    if (!chatId) {
      throw new Error('缺少聊天 ID');
    }

    const sendResult = await apiService.sendMessageExternal(chatId, message, attachments, signal, messageId, messageIds, sourceChannel);
    if (!sendResult) {
      throw new Error('發送訊息已中斷');
    }
    const response = sendResult.data;

    // 後端 HTTP 多為 200；業務錯誤時 response.code 非 200，sendMessageExternal 會拋錯，於下方 catch 依 code 回傳訊息
    // 支援兩種結構：response.data.data（多一層）或 response.data（訊息本體直接在 data 下）
    const hasNestedData = response.data != null && response.data.data != null;
    const messageData = response.data?.data ?? response.data;
    const responseStructure = hasNestedData ? 'data.data' : 'data';
    logger.info('API Response', `目前 response 結構為 ${responseStructure}`, {
      chatId,
      responseStructure,
      hasNestedData,
    });

    // 快速受理 ack（change: unify-reply-completion-signal Section 4，僅
    // aiagent.send.async-accept-enabled=true 時後端才會回這個形狀）：/send 已受理但尚未有內容，
    // 不含 content/metadata 欄位。真正的答案交由 useChat 觸發補收查詢取得。
    //
    // ⚠️ 必須攔在下方 `!messageData` 那道防線「之前」，且要同時認兩種形狀（AGUI-1082）：
    // 後端 `ChatController` 回的 ack 是**扁平**的——`ApiResponse.data` 直接就是
    // `{status, chatId, userMessageId}`，比一般回覆少一層（一般回覆是 `data.data.content`）。
    // 因此 `messageData`（= `response.data?.data ?? response.data`）在 ack 形狀下必定是
    // undefined，若只檢查 `messageData.status` 就永遠命中不了，會先被 `!messageData` 攔走、
    // 記成 ERROR 並回傳 `_emptyReply`——語意錯誤（受理成功被當成「回覆為空」），還讓
    // useChat 的 isFastAcceptAck 永遠為 false，使「送出進行中不判死」的保護形同虛設。
    // 實測證據：UAT 壓測每一次送出都印出
    // `回應結構無 messageData，raw:{"chatId":...,"userMessageId":...,"status":"accepted"}`。
    if (response?.status === 'accepted' || messageData?.status === 'accepted') {
      logger.info('API Response', '快速受理 ack，轉補收流程取得答案', { chatId });
      return { _accepted: true };
    }

    if (!messageData) {
      // 結構完全不可辨識（response.data / response.data.data 皆空，且不是上方的受理 ack）：
      // 記 log 供除錯，交給 useChat 走補收流程，不把整包物件字串化顯示成一坨 JSON。
      // 受理 ack 既已在上方攔截，這裡維持 error 等級——它現在只在真正異常時才會響。
      logger.error('API', '回應結構無 messageData，轉補收流程', {
        chatId,
        raw: JSON.stringify(response.data || response),
      });
      return { _emptyReply: true };
    }

    const metadata = messageData.metadata;
    const toolList = metadata?.tool_list;
    // MCP Apps 互動畫面（AGUI-1274）：阻塞模式下標記掛在 tool_list[].tool_calls[].mcpApp，
    // 抽出後隨回覆帶回 useChat 掛到 bot 訊息上（fast-accept 模式則改由 /reply 的 data.mcpApps 帶回）
    const mcpApps = extractMcpAppsFromToolList(toolList);
    const withMcpApps = (result) => (mcpApps.length > 0 ? { ...result, mcpApps } : result);

    // 檢查是否有圖表工具
    const hasPieChart = toolList && toolList.some(tool => tool.name === 'create_pie_chart');
    const hasLineChart = toolList && toolList.some(tool => tool.name === 'create_bar_chart');

    // 檢查是否有轉接真人客服（支援 tool.content 為字串或陣列 content[0].text）
    const hasTransferToHumanAgent = toolList && toolList.some(tool => {
      if (tool.name !== 'transfer_to_human_agent' || !tool.content) return false;
      // 轉接真人客服後，標記目前正在使用 WebSocket
      try {
        if (typeof window !== 'undefined') {
          void appStorage.setItem('nowIsWebSocket', 'true');
        }
      } catch (error) {
        logger.warn("WebSocket", "設定 nowIsWebSocket 為 true 時失敗，將忽略此錯誤", {
          error: error.message,
        });
      }

      try {
        const raw = Array.isArray(tool.content)
          ? (tool.content[0]?.text ?? tool.content[0])
          : tool.content;
        const contentData = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return contentData && contentData.actionCode === 'TRANSFER_SUCCESS';
      } catch (error) {
        console.error('解析轉接工具內容失敗:', error);
        return false;
      }
    });

    logger.info('WebSocket 轉接判斷', hasTransferToHumanAgent ? '符合轉接成功，將建立 WebSocket' : '不符合轉接成功，不建立 WebSocket', {
      chatId,
      hasTransferToHumanAgent,
      hasToolList: !!toolList,
      toolListLength: toolList?.length ?? 0,
      transferToolFound: toolList?.some(t => t.name === 'transfer_to_human_agent') ?? false,
    });

    if (hasPieChart || hasLineChart) {
      const textContent = messageData.content?.text ?? '';
      const chartTool = toolList?.find(tool =>
        tool.name === 'create_pie_chart' || tool.name === 'create_bar_chart'
      );
      if (chartTool && chartTool.content) {
        try {
          const raw = Array.isArray(chartTool.content)
            ? (chartTool.content[0]?.text ?? chartTool.content[0])
            : chartTool.content;
          const chartData = typeof raw === 'string' ? JSON.parse(raw) : raw;
          return withMcpApps({
            text: textContent,
            hasChart: true,
            chartData: chartData.data,
            chartTitle: chartData.config?.title,
            chartType: chartData.type || (chartTool.name === 'create_pie_chart' ? 'pie' : 'bar'),
            xlabel: chartData.config?.xlabel,
            ylabel: chartData.config?.ylabel
          });
        } catch (error) {
          console.error('解析圖表資料失敗:', error);
          return textContent;
        }
      }
    }

    if (hasTransferToHumanAgent) {
      logger.info('WebSocket 轉接判斷', '觸發 enableHumanAgentWebSocket，建立 WebSocket 連線', { chatId });
      window.dispatchEvent(new CustomEvent('enableHumanAgentWebSocket', {
        detail: { chatId }
      }));
      const transferMessage = messageData.content?.text || '正在為您轉接真人客服，請稍候...';
      return withMcpApps({
        text: transferMessage,
        hasTransferToHumanAgent: true
      });
    }

    // 一般回覆：後端可能回傳不同巢狀結構，依序嘗試常見欄位以取得可顯示文字。
    // 用 typeof 判斷「欄位存在且為字串」（含空字串），才能區分下列兩種情況：
    //   (a) 有內容的正常回覆 → 直接顯示
    //   (b) 欄位存在但內容為空字串 → 本次回覆沒有成功產生內容（例如同一 session 併發送出，
    //       GenAIus 對第二個請求空手而回），必須轉補收流程，不能把空內容或整包物件當答案顯示
    const textCandidates = [
      messageData.content?.text,
      messageData.text,
      response.data?.content?.text,
      response.data?.text,
      response.text,
    ];
    // 第一個「有實際內容」的字串即為正常回覆
    const nonEmptyText = textCandidates.find(
      (t) => typeof t === 'string' && t.trim().length > 0
    );
    // 帶互動畫面時改回傳物件（{ text, mcpApps }）；沒有時維持回傳字串，既有呼叫端行為不變
    if (nonEmptyText !== undefined) return mcpApps.length > 0 ? { text: nonEmptyText, mcpApps } : nonEmptyText;
    // 沒有文字但有互動畫面：畫面本身就是這一輪的回覆（等使用者在畫面上選擇），照常落地，不轉補收
    if (mcpApps.length > 0) return { text: '', mcpApps };

    // 走到這裡代表沒有任何非空回覆文字：不論是「有 text 欄位但為空」或「結構無法辨識」，
    // 都不把原始物件字串化顯示（避免使用者看到一坨 JSON），改回傳空回覆標記交給 useChat 轉補收
    logger.warn('API', 'AI 回覆內容為空，轉補收流程', {
      chatId,
      raw: JSON.stringify(response.data || response),
    });
    return { _emptyReply: true };
  } catch (error) {
    console.error('外部 API 錯誤:', error);
    const errMsg = error?.message ? ` (${error.message})` : '';

    // 1. 自訂錯誤（前端參數/流程問題，非網路層級，重送也無法補收）
    if (error.message === '缺少聊天 ID') {
      return `系統參數錯誤，請重新開啟對話。${errMsg}`;
    }
    if (error.message === '發送訊息已中斷') {
      return `發送訊息已中斷，請稍後再試。${errMsg}`;
    }

    // 2. JSON 解析／非 JSON 回應：代表後端有回應但格式不可解析，非連線層級問題
    if (error instanceof SyntaxError) {
      return `資料解析錯誤，請稍後再試。${errMsg}`;
    }
    if (error.message?.startsWith?.('Invalid JSON response')) {
      return `資料解析錯誤，請稍後再試。${errMsg}`;
    }

    // 2.5. BFF 代理層自身失敗（逾時／IO 錯誤／上游非 2xx），被 BFF 包裝成 HTTP 200 + 業務碼 500
    // 的 JSON，外觀跟後端真的回覆的業務錯誤一模一樣（見 BffProxyService.buildApiResponseErrorJson，
    // 目的是避免前端 JSON.parse 對上游 504/HTML 之類的東西炸掉）。若照下面分支 3 的邏輯，會誤判成
    // 「後端已明確回應、答案已定案」，把這句提示文字當成 AI 的正式回覆寫進聊天紀錄——但實際上
    // mygenieap 當下很可能仍在處理（例如 BFF 逾時秒數跟 mygenieap 內部逾時幾乎相同、BFF 先斷），
    // 答案可能已經/即將寫進暫存表，必須走補收才能撈回來，不能當成定案內容顯示。
    // 用 BFF 自己加的 X-BFF-Proxy-Error／X-BFF-Upstream-Status 這兩個 header 辨識（apiService.js
    // 的 request() 已把 response header 收進 http.headers），區分「BFF 自己造的假業務回應」跟
    // 「mygenieap 真正回的業務錯誤」。
    const bffHeaders = error.apiResponse?.http?.headers || {};
    if (bffHeaders['x-bff-proxy-error'] || bffHeaders['x-bff-upstream-status']) {
      logger.warn('API', 'BFF 代理層失敗（逾時／IO錯誤），轉補收流程', {
        chatId,
        bffProxyError: bffHeaders['x-bff-proxy-error'],
        bffUpstreamStatus: bffHeaders['x-bff-upstream-status'],
      });
      return { _sendFailed: true, text: error.message || `抱歉，訊息好像沒有成功送出，請稍後再試一次喔。${errMsg}` };
    }

    // 3. 後端已明確回應（401/403/404/400/5xx 等）：業務邏輯已判斷完畢，代表請求確實有到達，
    // 非「連線中斷、答案可能已在後端」的情境，不需要補收。直接顯示後端給的原因（error.message
    // 即為 ApiResponse.message），前端不再依業務碼/HTTP 狀態另外猜測或改寫文字
    // （change: uniform-error-display）。
    if (error.http || error.apiResponse) {
      return error.message || '抱歉，發生了一些錯誤，請稍後再試一次喔！';
    }

    // 4. 其餘錯誤（沒有 `.http`/`.apiResponse`，即從未收到後端回應），一律視為「連線層級失敗」，
    // 預設走補收（黑名單排除、非白名單認定）：無法判斷後端當下是否已收到、是否仍在處理、
    // 甚至是否已經處理完畢並暫存——這正是補收流程存在的目的，交給
    // GET /web/chat/{chatId}/reply 的三態回應去判斷，比在這裡用 error.name/訊息內容
    // 做白名單比對更可靠：手機真實網路環境下連線中斷產生的錯誤形狀不見得符合
    // AbortError/ECONNABORTED/特定英文關鍵字，只要沒被列進白名單就會誤判成無法補收，
    // 讓一句通用錯誤訊息被當成正式回覆寫死（見 [補收] 相關調查）。
    // 即使實際上訊息真的沒送到後端，補收查詢也會誠實回 not_found，最終仍會顯示失敗訊息，
    // 只是多一次網路來回，不會讓真正失敗的情況被誤判成功。
    return { _sendFailed: true, text: `抱歉，訊息好像沒有成功送出，請稍後再試一次喔。${errMsg}` };
  }
};


/** Microsoft 第三方服務連結狀態（既有端點，Sidebar 的設定視窗也在用）。 */
export const MICROSOFT_STATUS_PATH = '/web/integrations/microsoft/status';

/**
 * 查詢 Outlook 等 Microsoft 服務的連結狀態（AGUI-1113 A1 前置鎖定）。
 *
 * 回傳的是後端透傳 AuthGateway 的原始形狀 `{ types: { outlook: { status }, ... } }`，
 * 不在這裡壓成布林值——`status` 除了 `active` 還有其他值，壓扁後就分不出來了。
 * 判斷是否已連結請用 {@link isOutlookConnected}。
 *
 * 走 {@link authorizedRequest}：業務碼 401（MyBro 登入 token 過期，預設 60 分鐘）先 refresh 再重送一次。
 * 頁面每分鐘的背景同步也靠這裡換新 token，使用者把頁面開著也能持續看到正確狀態；
 * 原本直接打 apiService.request，401 時 `data` 為空，被誤判成「未連結」（10-20 UAT 回報）。
 *
 * ⛔ 非 PCS 網域的帳號會被後端 `requirePcsDomain` 擋下（業務碼 403），
 * 這時一律當作「尚未連結」，**不可**把 domain 原因顯示給使用者（AC-SUB-01-12）。
 * 其他非 200 業務碼一律丟錯，由呼叫端當「查不到」，⛔ 不可當成未連結。
 *
 * @returns {Promise<{types?: Object}|null>}
 */
export const getMicrosoftIntegrationStatus = async () => {
  const response = await authorizedRequest(MICROSOFT_STATUS_PATH);
  const code = typeof response?.code === 'number' ? response.code : 200;
  if (code === 403) return null;
  if (code !== 200) {
    throw new Error(response?.message || `查詢 Outlook 連結狀態失敗（${code}）`);
  }
  return response?.data ?? null;
};

/** Outlook 是否已連結。形狀與 Sidebar.js 解析 msIntegrationStatus 的方式一致。 */
export const isOutlookConnected = (status) => status?.types?.outlook?.status === 'active';

/*
 * ---- 信件即時通知（SA v2.0 §5.1）：mygenieap 代理 MyAgent APISIX ----
 * AGUI-1246 起只剩這一套：舊架構（MyBro 自己訂閱 Graph、規則存 MyBro DB）的 API 與畫面已移除，沒有 feature flag 分流。
 * 寫入都帶 rulesVersion，版本不符時回 409 VERSION_CONFLICT。
 * ⛔ 沒有修改規則的 API：本期不做規則修改（決議 #6），要改就刪掉重建。
 */

/** 規則的新增、啟用切換、刪除；契約見 mygenieap 的 MailNotifyRuleController */
export const MAIL_RULES_PATH = '/web/integrations/microsoft/mail-rules';

/** 狀態與規則合併在同一支 GET（狀態卡與規則區同時出現、同時更新） */
export const MAIL_NOTIFY_PATH = '/web/integrations/microsoft/mail-notify';

/**
 * 信件即時通知寫入失敗。`errorCode` 是 APISIX 的 `error`（例如 VERSION_CONFLICT），畫面依它挑文案（SA C12 錯誤表）；
 * `code` 是 AP 回的業務碼（APISIX 的 HTTP 狀態，5xx 與逾時一律 503）。
 */
export class MailNotifyError extends Error {
  constructor(code, errorCode, message) {
    super(message || '操作失敗，請稍後再試');
    this.name = 'MailNotifyError';
    this.code = code;
    this.errorCode = errorCode || null;
  }
}

/**
 * 共用：code 200 → `{ pending: false, data }`；code 202（APISIX 還沒有訂閱操作的結果）→ `{ pending: true, data }`；
 * 其餘一律丟 {@link MailNotifyError}。⛔ 202 不是失敗也不是成功，呼叫端要輪詢 GET 才知道結果（SA 情境 L）。
 * 業務碼 401 的 refresh 重送由 authorizedRequest 處理。
 */
const mailNotifyRequest = async (endpoint, method, body) => {
  const response = await authorizedRequest(endpoint, { method, body });
  if (!response) throw new MailNotifyError(0, null, null);
  const code = typeof response.code === 'number' ? response.code : 200;
  if (code === 200) return { pending: false, data: response.data ?? null };
  if (code === 202) return { pending: true, data: response.data ?? null };
  throw new MailNotifyError(code, response.errorCode ?? response.data?.errorCode, response.message);
};

/** `{state, reason, rulesVersion, operation, maxRules, rules[], notify}`；只取 data，失敗丟例外 */
export const getMailNotify = async () => (await mailNotifyRequest(MAIL_NOTIFY_PATH, 'GET')).data;

export const createMailRuleV2 = (rule, rulesVersion) =>
  mailNotifyRequest(MAIL_RULES_PATH, 'POST', { ...rule, rulesVersion });

export const setMailRuleEnabledV2 = (ruleId, enabled, rulesVersion) =>
  mailNotifyRequest(`${MAIL_RULES_PATH}/${encodeURIComponent(ruleId)}`, 'PATCH', { enabled, rulesVersion });

export const deleteMailRuleV2 = (ruleId, rulesVersion) =>
  mailNotifyRequest(
    `${MAIL_RULES_PATH}/${encodeURIComponent(ruleId)}?rulesVersion=${encodeURIComponent(rulesVersion ?? '')}`,
    'DELETE',
  );

/** 恢復通知：AP 把第一條啟用中的規則原封不動 PUT 回去，帶當下 session 的憑證（US-NTF-02） */
export const restoreMailNotify = (rulesVersion) =>
  mailNotifyRequest(`${MAIL_NOTIFY_PATH}/restore`, 'POST', { rulesVersion });

/** 電話通知開關（US-NTF-03）；成功回 `{rulesVersion, notify}` */
export const setMailNotifyPhone = (phone, rulesVersion) =>
  mailNotifyRequest(`${MAIL_NOTIFY_PATH}/settings`, 'PUT', { phone, rulesVersion });
